import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  FlexColumn,
  formFieldRequiredLabel as requiredLabel,
  TextField,
} from '@elementary/common-ui/components';
import FormControls from '@elementary/common-ui/components/FormControls';
import { Autocomplete, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import {
  FetchMicrosoftTeamsWebhooksApi,
  MicrosoftTeamsForm,
  MicrosoftTeamsWebhook,
} from 'shared/types';
import { parseIntegrationError } from 'settings/utils';
import { useMicrosoftTeamsIntegration } from 'shared/hooks/integrations';
import IntegrationDialog from '../../shared/Integration/IntegrationDialog';
import TestIntegrationSection from './IntegrationTestSections/TestIntegrationSection';
import MicrosoftTeamsTestSection from './IntegrationTestSections/MicrosoftTeamsTestSection';
import WebhooksArray from './WebhooksArray';

type Props = {
  open: boolean;
  onClose: () => void;
  data?: FetchMicrosoftTeamsWebhooksApi;
  loading?: boolean;
  envId: string;
  onDisconnect?: () => void;
};

const validateDefaultChannel = (
  value: string,
  currentWebhooks: MicrosoftTeamsWebhook[],
) => currentWebhooks.map((webhook) => webhook.name).includes(value);

const TeamsDialog = ({
  open,
  onClose,
  loading,
  envId,
  onDisconnect,
}: Props) => {
  const [testSuccess, setTestSuccess] = useState(false);
  const {
    microsoftTeamsWebhooksQuery,
    microsoftTeamsDefaultsQuery,
    microsoftTeamsWebhooksMutation,
    microsoftTeamsDefaultsMutation,
    isTeamsLoading,
  } = useMicrosoftTeamsIntegration({ envId });

  const webhooks = microsoftTeamsWebhooksQuery.data?.webhooks || [];
  const defaultDestination =
    microsoftTeamsDefaultsQuery.data?.defaultDestination;

  const defaultValues = {
    webhooks: webhooks.length !== 0 ? webhooks : [{ name: '', url: '' }],
    defaultWebhookName: defaultDestination?.defaultWebhookName,
  };

  const methods = useForm<MicrosoftTeamsForm>({
    defaultValues,
    mode: 'onBlur',
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
  } = methods;

  const {
    mutateAsync: setTeamsWebhooks,
    isError: webhooksError,
    error: webhooksErrorData,
    isLoading: isLoadingWebhooks,
  } = microsoftTeamsWebhooksMutation;

  const {
    mutate: setTeamsDefaults,
    isError: defaultsError,
    isLoading: isLoadingDefaults,
  } = microsoftTeamsDefaultsMutation;

  const missingWebhooks = useMemo<string[]>(
    () => webhooksErrorData?.response?.data?.extra?.missing_webhooks || [],
    [webhooksErrorData],
  );

  const getOptions = () =>
    methods
      .watch('webhooks')
      .filter((field) => field.name && field.url)
      .map((field) => field.name)
      .sort();

  return (
    <IntegrationDialog
      open={open}
      onClose={onClose}
      title="Connect Microsoft Teams (webhook)"
      titleChip={<Chip label="Deprecated" color="default" size="small" />}
      onSubmit={handleSubmit(async (formData: MicrosoftTeamsForm) => {
        await setTeamsWebhooks(formData);
        setTeamsDefaults(formData);
        onClose();
      })}
      isValid={isValid && !isTeamsLoading}
      error={
        parseIntegrationError(webhooksError) ||
        parseIntegrationError(defaultsError)
      }
      loading={loading || isLoadingWebhooks || isLoadingDefaults}
      width={972}
    >
      <FlexColumn sx={{ gap: 3 }}>
        <Alert severity="info">
          The Microsoft Teams app is now recommended for new setups. Webhooks
          remain supported for existing configurations.
        </Alert>

        <Typography sx={{ color: 'content.secondary' }}>
          Configure webhooks for each channel on Microsoft Teams you want to get
          alerts to. For detailed instructions, check our docs.
        </Typography>

        <WebhooksArray {...methods} />

        <FlexColumn gap={1}>
          <Alert severity="info">
            Note - webhooks are saved across all environments.
          </Alert>
          {missingWebhooks.length > 0 && (
            <Alert severity="error">
              The following webhooks are configured in alert rules, but are
              missing from the configuration
              {missingWebhooks.map((webhook) => (
                <li key={webhook}>{webhook}</li>
              ))}
            </Alert>
          )}
        </FlexColumn>

        <FormControls.InputWrapper>
          <Autocomplete
            options={getOptions()}
            loadingText="Loading channels..."
            loading={microsoftTeamsDefaultsQuery.isLoading}
            defaultValue={defaultDestination?.defaultWebhookName}
            onSelect={register('defaultWebhookName').onChange}
            renderInput={(params) => (
              <TextField
                label="Select default Teams channel"
                {...params}
                {...register('defaultWebhookName', {
                  required: requiredLabel,
                  validate: (value, formValue) =>
                    validateDefaultChannel(value, formValue.webhooks),
                })}
                size="large"
              />
            )}
          />
          <FormControls.ErrorMessage>
            {errors.defaultWebhookName?.message}
          </FormControls.ErrorMessage>
        </FormControls.InputWrapper>

        <TestIntegrationSection isSuccess={testSuccess}>
          <MicrosoftTeamsTestSection
            teamsWebhooks={watch('webhooks')}
            onSuccess={() => setTestSuccess(true)}
            onReset={() => setTestSuccess(false)}
          />
        </TestIntegrationSection>

        {onDisconnect && (
          <Button
            variant="text"
            color="error"
            onClick={onDisconnect}
            sx={{ alignSelf: 'flex-start', mt: 1 }}
          >
            Disconnect Microsoft Teams
          </Button>
        )}
      </FlexColumn>
    </IntegrationDialog>
  );
};

export default TeamsDialog;
